import { useState, useCallback } from 'react'
import { ITeamMember } from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

export interface IUseTeamMemberApiState {
  teamMembers: ITeamMember[]
  getTeamMembers: () => Promise<ITeamMember[]>
  createTeamMember: (teamMember: RecursivePartial<ITeamMember>) => Promise<ITeamMember>
  updateTeamMember: (teamMember: RecursivePartial<ITeamMember>, id: number) => Promise<ITeamMember>
  getUserByCellPhone: (cellPhone: ITeamMember['cellPhone']) => ITeamMember | undefined
}

/**
 * Sometimes the backend responds with null firstNames and lastNames. This is undefined behaviour. We're attempting
 * to prevent this going forward in fleet planner with frontend validation. This ensures that firstName and lastName
 * are strings for any existing team member parent user data.
 */
const nullCoalescedTeamMemberNames = (teamMember: ITeamMember): ITeamMember => ({
  ...teamMember,
  firstName: teamMember.firstName ?? '',
  lastName: teamMember.lastName ?? '',
})

export const useTeamMembersApi = (): IUseTeamMemberApiState => {
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([])

  const getTeamMembers = useCallback(async (): Promise<ITeamMember[]> => {
    const response = await Api.fetchAllTeamMembers()
    const { teamMembers: members = [] } = response || {}
    const recievedTeamMembers = members.map(tm => nullCoalescedTeamMemberNames(tm))
    setTeamMembers(recievedTeamMembers)
    return recievedTeamMembers
  }, [])

  const createTeamMember = useCallback(async (teamMember: RecursivePartial<ITeamMember>): Promise<
    ITeamMember
  > => {
    const response = await Api.createTeamMember(teamMember)
    const createdTeamMember = nullCoalescedTeamMemberNames(response.teamMember)
    createdTeamMember.canEdit = true
    return createdTeamMember
  }, [])

  const updateTeamMember = useCallback(
    async (teamMember: RecursivePartial<ITeamMember>, id: number): Promise<ITeamMember> => {
      const response = await Api.updateTeamMember(teamMember, id)
      const updatedTeamMember = nullCoalescedTeamMemberNames(response.teamMember)
      updatedTeamMember.canEdit = true
      return updatedTeamMember
    },
    []
  )

  /**
   * Helper method grabs the matching result of `teamMember` with typed arguments
   */
  const getUserByProp = useCallback(
    <K extends keyof ITeamMember>(keyName: K, val: ITeamMember[K]): ITeamMember | undefined => {
      return teamMembers.find(tm => tm[keyName] === val)
    },
    [teamMembers]
  )

  /**
   * Returns a user with a matching cellPhone number
   */
  const getUserByCellPhone = useCallback(
    (cellPhone: ITeamMember['cellPhone']): ITeamMember | undefined => {
      return getUserByProp('cellPhone', cellPhone)
    },
    [getUserByProp]
  )

  return {
    getTeamMembers,
    createTeamMember,
    updateTeamMember,
    teamMembers,
    getUserByCellPhone,
  }
}
